import { TableWithStore } from '@components/table/StoreTable/TableWithStore';
import { ThemeProvider } from '@mui/system';

import { useDeleteIndication, useSearchIndication } from '@services/indications';
import composeTheme from '@theme/theme';
import { useMemo, useState } from 'react';
import * as styles from '@components/dataGrid/FullPageDataGrid.styles';
import { IndicationSearchRequestQuery, IndicationSearchResponse } from '@lio/api-types';
import { Box } from '@mui/material';

import { getIndicationColumns, IndicationRow } from './getIndicationColumns';
import { IndicationColumnField } from '@pages/Quotes/IndicationsTable/IndicationColumnField.enum';
import { enqueueSnackbar } from 'notistack';

import { ConvertToQuoteDialog } from '@pages/IndicationDetails/ConvertToQuoteDialog';
import { ConfirmationDialog } from '@components/dialog/ConfirmationDialog';
import { SortIndicationQueryParam } from '@pages/Quotes/IndicationsTable/SortIndicationQueryParam';

type TIndicationTable = {
  items: IndicationSearchResponse['items'];
  params: IndicationSearchRequestQuery;
};

export const IndicationsDataGrid = () => {
  const [dialog, setDialog] = useState<{
    type: 'convertToQuote' | 'confirmDelete' | undefined;
    indication: undefined | IndicationRow;
  }>({
    type: undefined,
    indication: undefined,
  });

  const { deleteIndication, isPending } = useDeleteIndication();

  const handleDeleteIndication = async () => {
    const result = await deleteIndication({ indicationId: dialog!.indication!.id });
    if (result.status === 204) {
      enqueueSnackbar('Indication deleted successfully', { variant: 'success' });
      setDialog({ indication: undefined, type: undefined });
    } else {
      enqueueSnackbar('Failed to delete indication', { variant: 'error' });
    }
  };

  const handleOpenModal = ({
    type,
    indication,
  }: {
    type: 'confirmDelete' | 'convertToQuote';
    indication: IndicationRow;
  }) => {
    setDialog({ type, indication });
  };

  const columns = useMemo(() => {
    return getIndicationColumns({
      onOpenModal: handleOpenModal,
    });
  }, []);

  const columnSelector = columns.reduce((acc: any, col) => {
    acc[col.field] = col.headerName;
    return acc;
  }, {});

  return (
    <ThemeProvider theme={composeTheme('light')}>
      <Box sx={{ ...styles.dataGridContainer }}>
        <TableWithStore<TIndicationTable>
          tableId={`indications-table-store`}
          storeProps={{
            columnSelectorConfig: {
              columnSelector: {
                indication: columnSelector,
              },
              selectedTab: 'indication',
              defaultDisabledKey: IndicationColumnField.IndicationNumber,
              hiddenFromSelectionKeys: [IndicationColumnField.Menu],
              defaultHiddenColumns: {},
            },
          }}
          tableProps={{
            ariaLabel: `indication-table`,
            onLoadData: useSearchIndication,
            columns,
            isInitiating: false,
            filterConfig: {
              filterQueryParam: { searchText: 'searchText' },
              displayMode: 'Modal',
              ignoreQueryParams: ['quoteType'],
            },
            sortConfig: {
              sortQueryParam: SortIndicationQueryParam,
              defaultSort: { field: IndicationColumnField.IndicationNumber, sort: 'desc' },
            },
            rowConfig: {
              getRowId: ({ row }) => {
                return row?.id ?? '';
              },
            },
            sxProps: { dataGrid: styles.dataGrid },
            paginationConfig: { currentPage: 0, rowsPerPage: 25, pageOffset: 0 },
            noResultsConfig: {
              noResultsMessage: 'There are currently no policies to display',
            },
          }}
        />
      </Box>
      {dialog.type === 'confirmDelete' && dialog.indication && (
        <ConfirmationDialog
          isOpen={true}
          description={`Are you sure you want to delete Indication #${dialog.indication.referenceId}?`}
          isLoading={isPending}
          onCancel={() => setDialog({ indication: undefined, type: undefined })}
          buttonConfig={{
            confirmText: 'Yes, Delete',
            onConfirm: handleDeleteIndication,
            isConfirmButtonDisabled: isPending,
            secondaryText: 'Exit',
            isSecondaryButtonDisabled: false,
            onSecondaryButtonClick: () => setDialog({ indication: undefined, type: undefined }),
          }}
        />
      )}
      {dialog.type === 'convertToQuote' && dialog.indication && (
        <ConvertToQuoteDialog
          isOpen={true}
          handleClose={() => setDialog({ indication: undefined, type: undefined })}
          indicationId={dialog.indication.id}
        />
      )}
    </ThemeProvider>
  );
};
